import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CollapseSection,
  LabeledValue,
  Modal,
  RichTextValue,
  useFormatting,
} from "@msys/ui";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  DefineItemAttributeOverridesEntryInput,
  ItemChildrenVisibility,
} from "../../../../clients/graphqlTypes.js";
import { DurationField } from "../../../commons/form-fields/DurationField.js";
import { RichTextField } from "../../../commons/form-fields/RichTextField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  ItemAttributesOverrideModal_ItemFragment,
  useDefineItemAttributeOverridesMutation,
  useItemAttributesOverrideModalQuery,
} from "./ItemAttributesOverrideModal.generated.js";

type FormValues = {
  titleOverride: string | null;
  descriptionOverride: string | null;
  estimatedTimeOverride: number | null;
  childrenVisibilityOverride: ItemChildrenVisibility | null;
};

interface Props {
  templateId: string;
  itemId: string;
  versionNumber: number;
  handleClose(): void;
}

export const ItemAttributesOverrideModal = (props: Props) => {
  const client = useApolloClient();
  const query = useItemAttributesOverrideModalQuery({
    client,
    variables: {
      docId: props.templateId,
      itemId: props.itemId,
      versionNumber: props.versionNumber,
      applyOverrides: false,
    },
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const item = quoteTemplate?.item?.[0];

  if (!item) return null;

  return <ItemAttributesOverrideInnerModal item={item} {...props} />;
};

export const ItemAttributesOverrideInnerModal = ({
  templateId,
  itemId,
  versionNumber,
  handleClose,
  item,
}: Props & { item: ItemAttributesOverrideModal_ItemFragment }) => {
  const { t } = useTranslate(["InvoiceDetailsForm", "QuoteItem", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [defineItemAttributeOverride] = useDefineItemAttributeOverridesMutation(
    { client }
  );

  // ---- title

  const titleOrig = item.title;
  const titleOverride = item.itemAttributeOverrides.find(
    o => o.attribute === "title"
  );
  const titleOverrideValue =
    titleOverride?.valueAsJson !== null &&
    titleOverride?.valueAsJson !== undefined
      ? JSON.parse(titleOverride.valueAsJson)
      : null;

  // ---- description

  const descriptionOrig = item.description;
  const descriptionOverride = item.itemAttributeOverrides.find(
    o => o.attribute === "description"
  );
  const descriptionOverrideValue =
    descriptionOverride?.valueAsJson !== null &&
    descriptionOverride?.valueAsJson !== undefined
      ? JSON.parse(descriptionOverride.valueAsJson)
      : null;

  // ---- estimatedTime

  const estimatedTimeOrig = item.proposedCalculation?.timePerUnit ?? 0;
  const estimatedTimeOverride = item.itemAttributeOverrides.find(
    o => o.attribute === "estimatedTime"
  );
  const estimatedTimeOverrideValue =
    estimatedTimeOverride?.valueAsJson !== null &&
    estimatedTimeOverride?.valueAsJson !== undefined
      ? JSON.parse(estimatedTimeOverride.valueAsJson)
      : null;

  // ---- childrenVisibility

  const isSectionOrDecision =
    item.type === "section" || item.type === "decision";
  const childrenVisibilityOrig = item.childrenVisibility;
  const childrenVisibilityOverride = item.itemAttributeOverrides.find(
    o => o.attribute === "childrenVisibility"
  );
  const childrenVisibilityOverrideValue =
    childrenVisibilityOverride?.valueAsJson !== null &&
    childrenVisibilityOverride?.valueAsJson !== undefined
      ? (JSON.parse(
          childrenVisibilityOverride.valueAsJson
        ) as ItemChildrenVisibility)
      : null;

  const childrenVisibilityOptions: Array<{
    value: ItemChildrenVisibility;
    label: string;
  }> = [
    {
      value: "SHOW_CHILDREN",
      label: t("SHOW_CHILDREN", { ns: "QuoteItem" }),
    },
    {
      value: "SHOW_CHILDREN_HIDE_PRICE",
      label: t("SHOW_CHILDREN_HIDE_PRICE", { ns: "QuoteItem" }),
    },
    {
      value: "HIDE_CHILDREN",
      label: t("HIDE_CHILDREN", { ns: "QuoteItem" }),
    },
  ];

  const initialValues = {
    titleOverride: titleOverrideValue,
    descriptionOverride: descriptionOverrideValue,
    estimatedTimeOverride: estimatedTimeOverrideValue,
    childrenVisibilityOverride: childrenVisibilityOverrideValue,
  };

  const handleSubmit = async (values: FormValues) => {
    const overrides: Array<DefineItemAttributeOverridesEntryInput> = [];

    if (!item.isRootItem) {
      overrides.push({
        attribute: "title",
        valueAsJson:
          values.titleOverride !== null
            ? JSON.stringify(values.titleOverride)
            : null,
      });
    }

    overrides.push({
      attribute: "description",
      valueAsJson:
        values.descriptionOverride !== null
          ? JSON.stringify(values.descriptionOverride)
          : null,
    });

    if (item.type === "paid") {
      overrides.push({
        attribute: "estimatedTime",
        valueAsJson:
          values.estimatedTimeOverride !== null
            ? JSON.stringify(values.estimatedTimeOverride)
            : null,
      });
    }

    if (isSectionOrDecision) {
      overrides.push({
        attribute: "childrenVisibility",
        valueAsJson:
          values.childrenVisibilityOverride !== null
            ? JSON.stringify(values.childrenVisibilityOverride)
            : null,
      });
    }

    await defineItemAttributeOverride({
      variables: {
        input: {
          itemId,
          templateId,
          overrides,
          versionNumber,
        },
      },
    });

    // we need refetch observable queries since applyOverrides are not in normalized cache
    await client.reFetchObservableQueries();
    enqueueSnackbar(t("Overrides applied", { ns: "QuoteItem" }));
    handleClose();
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        titleOverride: Yup.string()
          .label(t("Title", { ns: "QuoteItem" }))
          .nullable(),
        descriptionOverride: Yup.string()
          .label(t("Description", { ns: "QuoteItem" }))
          .nullable(),
        estimatedTimeOverride: Yup.number()
          .label(t("Time / Unit", { ns: "InvoiceDetailsForm" }))
          .min(0)
          .nullable(),
        childrenVisibilityOverride: Yup.string()
          .label(t("Children visibility", { ns: "QuoteItem" }))
          .nullable(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);
  const { getFormattedDuration } = useFormatting();

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
    >
      {({ isSubmitting, dirty, isValid, values, setFieldValue }) => (
        <Modal
          title={t("Configure item attributes", {
            ns: "QuoteItem",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Save and apply", {
                ns: "Global",
              }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty || !isValid,
              },
            },
          ]}
        >
          <Stack direction="column" spacing={2}>
            <CollapseSection
              title={t("Template item default values", {
                ns: "QuoteItem",
              })}
            >
              <Stack direction="column" spacing={1}>
                {!item.isRootItem && (
                  <LabeledValue label={t("Title", { ns: "QuoteItem" })}>
                    {titleOrig || "–"}
                  </LabeledValue>
                )}
                <LabeledValue label={t("Description", { ns: "QuoteItem" })}>
                  {descriptionOrig ? (
                    <RichTextValue
                      key={descriptionOrig}
                      htmlContent={descriptionOrig}
                    />
                  ) : (
                    "–"
                  )}
                </LabeledValue>
                {item.type === "paid" && (
                  <LabeledValue label={t("Work", { ns: "QuoteItem" })}>
                    {t("Time / Unit", {
                      ns: "InvoiceDetailsForm",
                    })}
                    : {getFormattedDuration(estimatedTimeOrig)} h
                  </LabeledValue>
                )}
                {isSectionOrDecision && (
                  <LabeledValue
                    label={t("Children visibility", { ns: "QuoteItem" })}
                  >
                    {childrenVisibilityOptions.find(
                      o => o.value === childrenVisibilityOrig
                    )?.label ?? childrenVisibilityOrig}
                  </LabeledValue>
                )}
              </Stack>
            </CollapseSection>
            <CollapseSection
              title={t("Template item applied values", {
                ns: "QuoteItem",
              })}
            >
              <Form id={formId}>
                <Stack direction="column" spacing={1}>
                  {!item.isRootItem && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TextField
                        value={
                          values.titleOverride === null ? titleOrig : undefined
                        }
                        name="titleOverride"
                        label={t("Title", { ns: "QuoteItem" })}
                      />
                      <IconButton
                        color="primary"
                        size="small"
                        sx={{ flexGrow: 0, flexShrink: 0 }}
                        disabled={values.titleOverride === null}
                        onClick={async () => {
                          await setFieldValue("titleOverride", null);
                        }}
                      >
                        <Tooltip title={t("Reset", { ns: "Global" })}>
                          <RestartAltIcon />
                        </Tooltip>
                      </IconButton>
                    </Stack>
                  )}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box flex={1}>
                      <RichTextField
                        value={
                          values.descriptionOverride === null
                            ? descriptionOrig
                            : undefined
                        }
                        name="descriptionOverride"
                        label={t("Description", { ns: "QuoteItem" })}
                      />
                    </Box>
                    <IconButton
                      color="primary"
                      size="small"
                      sx={{ flexGrow: 0, flexShrink: 0 }}
                      disabled={values.descriptionOverride === null}
                      onClick={async () => {
                        await setFieldValue("descriptionOverride", null);
                      }}
                    >
                      <Tooltip title={t("Reset", { ns: "Global" })}>
                        <RestartAltIcon />
                      </Tooltip>
                    </IconButton>
                  </Stack>
                  {item.type === "paid" && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h4">
                        {t("Work", { ns: "QuoteItem" })}
                      </Typography>
                      <Box display="flex" flex={1}>
                        <DurationField
                          value={
                            values.estimatedTimeOverride === null
                              ? estimatedTimeOrig
                              : undefined
                          }
                          name="estimatedTimeOverride"
                          label={t("Time / Unit", {
                            ns: "InvoiceDetailsForm",
                          })}
                          style={{ maxWidth: "120px" }}
                        />
                      </Box>
                      <IconButton
                        color="primary"
                        size="small"
                        sx={{ flexGrow: 0, flexShrink: 0 }}
                        disabled={values.estimatedTimeOverride === null}
                        onClick={async () => {
                          await setFieldValue("estimatedTimeOverride", null);
                        }}
                      >
                        <Tooltip title={t("Reset", { ns: "Global" })}>
                          <RestartAltIcon />
                        </Tooltip>
                      </IconButton>
                    </Stack>
                  )}
                  {isSectionOrDecision && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box flex={1}>
                        <SelectField<ItemChildrenVisibility>
                          value={
                            values.childrenVisibilityOverride === null
                              ? childrenVisibilityOrig
                              : undefined
                          }
                          name="childrenVisibilityOverride"
                          label={t("Children visibility", {
                            ns: "QuoteItem",
                          })}
                          options={childrenVisibilityOptions}
                        />
                      </Box>
                      <IconButton
                        color="primary"
                        size="small"
                        sx={{ flexGrow: 0, flexShrink: 0 }}
                        disabled={values.childrenVisibilityOverride === null}
                        onClick={async () => {
                          await setFieldValue(
                            "childrenVisibilityOverride",
                            null
                          );
                        }}
                      >
                        <Tooltip title={t("Reset", { ns: "Global" })}>
                          <RestartAltIcon />
                        </Tooltip>
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </Form>
            </CollapseSection>
          </Stack>
        </Modal>
      )}
    </Formik>
  );
};
